import React, { useState } from "react";
import AppShell from "@/components/AppShell";
import CheckoutModal from "@/components/CheckoutModal";
import { useTiers } from "@/lib/useTiers";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Lock, Sparkles, ArrowRight } from "lucide-react";

export default function Pathway() {
  const { user } = useAuth();
  const { tiers } = useTiers();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [targetTier, setTargetTier] = useState(null);

  const userTier = user?.tier || "free";
  const order = ["free", "workshop", "course", "membership", "cohort"];
  const userIdx = order.indexOf(userTier);

  // Sort tiers by their `grants_tier` within the 5-rung ladder; then by sort_order.
  // Append a Welcome "free" node at the top.
  const laddered = [...tiers].sort((a, b) => {
    const ao = order.indexOf(a.grants_tier || a.tier_key || a.key);
    const bo = order.indexOf(b.grants_tier || b.tier_key || b.key);
    return ao - bo;
  });
  const nodes = [
    { tier_key: "free", name: "Welcome", sub: "Free", features: ["Faith intro lesson", "Daily protocol"] },
    ...laddered,
  ];

  return (
    <AppShell>
      <div className="space-y-10">
        <div className="fade-up">
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#FF8A00]">The Pathway</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Your transformation, mapped.</h1>
          <p className="text-white/55 mt-2 max-w-2xl">
            A staircase — not a leap. Each tier builds the foundation for the next.
          </p>
        </div>

        <div className="relative">
          <div className="absolute left-5 top-6 bottom-6 w-px bg-gradient-to-b from-[#FF8A00]/40 via-white/10 to-transparent" />
          <div className="space-y-4">
            {nodes.map((n, i) => {
              const key = n.tier_key || n.key;
              const grants = n.grants_tier || key;
              const isFree = key === "free";
              const grantsIdx = order.indexOf(grants);
              const status = isFree || grantsIdx <= userIdx ? "active" : grantsIdx === userIdx + 1 ? "next" : "locked";
              const glow = status === "active";
              const unlocked = status === "active";
              const isCurrent = grants === userTier && !isFree;

              return (
                <div key={key}
                  data-testid={`pathway-${key}`}
                  className={`relative pl-14 pr-5 py-6 rounded-2xl transition-all ${
                    unlocked ? "bg-[#0A0A0A] border border-white/5" : "bg-[#070707] border border-white/5 opacity-70"
                  } ${isCurrent ? "border-[#FF8A00]/40" : ""} fade-up delay-${Math.min(i+1, 5)}`}>
                  <div className={`absolute left-0 top-6 w-11 h-11 rounded-full flex items-center justify-center ${
                    glow ? "bg-gradient-to-br from-[#FF8A00] to-[#E52E71] pulse-glow"
                         : status === "next" ? "bg-[#0F0F0F] border border-[#FF8A00]/40"
                         : "bg-[#0F0F0F] border border-white/10"
                  }`}>
                    {glow ? <CheckCircle2 size={18} /> : status === "next" ? <ArrowRight size={16} className="text-[#FF8A00]" /> : <Lock size={14} className="text-white/40" />}
                  </div>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display text-2xl">{n.name}</h3>
                        {isCurrent && <span className="text-[10px] tracking-[0.22em] uppercase bg-[#FF8A00]/15 text-[#FF8A00] px-2 py-1 rounded-full">current</span>}
                      </div>
                      <p className="text-sm text-white/55 mt-1">{n.sub}</p>
                      <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {n.features?.map((f) => (
                          <li key={f} className="flex items-start gap-2 text-sm text-white/70">
                            <Sparkles size={12} className="mt-1 text-[#FF8A00]" /> {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                    {!isFree && (
                      <div className="text-right">
                        <div className="font-display text-2xl">{n.price}</div>
                        <div className="text-[11px] text-white/40">{n.cadence}</div>
                        {status === "next" && (
                          <Button
                            onClick={() => { setTargetTier(key); setUpgradeOpen(true); }}
                            data-testid={`upgrade-${key}`}
                            className="mt-2 rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
                            Unlock
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <CheckoutModal open={upgradeOpen} onOpenChange={setUpgradeOpen} tierKey={targetTier} />
    </AppShell>
  );
}
